import { useEffect, useState } from "react";
import { Heart, MessageCircle, MapPin, Lightbulb, Trash2, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { travelApi } from "@/lib/travelApi";

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `hace ${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

export default function ExperienceCard({ exp, currentUser, onDelete }) {
  const [liked, setLiked] = useState(!!exp.liked_by_me);
  const [likes, setLikes] = useState(exp.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const isOwner = currentUser?.id && currentUser.id === exp.user_id;

  const toggleLike = async () => {
    if (!currentUser) {
      toast.error("Inicia sesión para reaccionar");
      return;
    }
    // optimistic
    setLiked((v) => !v);
    setLikes((n) => n + (liked ? -1 : 1));
    try {
      await travelApi.toggleLike(exp.id);
    } catch {
      setLiked((v) => !v);
      setLikes((n) => n + (liked ? 1 : -1));
      toast.error("No se pudo registrar tu reacción");
    }
  };

  useEffect(() => {
    if (!showComments) return;
    travelApi.listComments(exp.id).then(setComments).catch(() => setComments([]));
  }, [showComments, exp.id]);

  const submitComment = async (e) => {
    e.preventDefault();
    const body = commentText.trim();
    if (!body) return;
    if (!currentUser) {
      toast.error("Inicia sesión para comentar");
      return;
    }
    setPosting(true);
    try {
      const c = await travelApi.addComment(exp.id, body);
      setComments((cs) => [...cs, c]);
      setCommentText("");
    } catch {
      toast.error("No se pudo enviar el comentario");
    } finally {
      setPosting(false);
    }
  };

  const remove = async () => {
    if (!confirm("¿Eliminar esta experiencia?")) return;
    try {
      await travelApi.deleteExperience(exp.id);
      toast.success("Experiencia eliminada");
      onDelete?.(exp.id);
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <article
      data-testid={`exp-${exp.id}`}
      className="border hairline bg-cream-card rounded-2xl overflow-hidden hover:shadow-md transition"
    >
      {/* Header */}
      <header className="flex items-center gap-3 p-4 pb-2">
        <div className="w-9 h-9 rounded-full bg-coral/15 grid place-items-center text-[11px] font-mono text-coral overflow-hidden">
          {exp.user_avatar
            ? <img src={exp.user_avatar} alt={exp.user_name} className="w-full h-full object-cover" />
            : (exp.user_name?.[0]?.toUpperCase() || "V")}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{exp.user_name}</p>
          <p className="text-[11px] font-mono text-ink-mid">{fmtRelative(exp.created_at)}</p>
        </div>
        {isOwner && (
          <button
            onClick={remove}
            data-testid={`exp-${exp.id}-delete`}
            className="text-ink-mid hover:text-coral p-1.5 rounded-full hover:bg-cream"
            aria-label="Eliminar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </header>

      {/* Body */}
      <div className="px-4 pb-3">
        <h3 className="font-serif text-xl tracking-tight leading-tight">{exp.title}</h3>
        {exp.location_name && (
          <p className="text-[12px] font-mono text-coral mt-0.5 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {exp.location_name}
          </p>
        )}
        {exp.body && <p className="text-sm text-ink mt-2 whitespace-pre-line">{exp.body}</p>}
      </div>

      {/* Photos */}
      {exp.photos && exp.photos.length > 0 && (
        <div className={exp.photos.length === 1 ? "grid grid-cols-1" : "grid grid-cols-2 gap-0.5"}>
          {exp.photos.slice(0, 4).map((p, i) => (
            <div key={i} className="aspect-[4/3] bg-cream-deep overflow-hidden">
              <img
                src={p}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Tips */}
      {exp.tips && (
        <div className="mx-4 my-3 bg-coral/10 border-l-2 border-coral rounded-r-xl px-3 py-2">
          <p className="font-mono text-[9px] uppercase tracking-widest text-coral mb-0.5 inline-flex items-center gap-1">
            <Lightbulb className="w-3 h-3" /> Tip del viajero
          </p>
          <p className="text-[13px] leading-snug text-ink">{exp.tips}</p>
        </div>
      )}

      {/* Actions */}
      <footer className="flex items-center gap-1 px-2 py-2 border-t hairline">
        <button
          onClick={toggleLike}
          data-testid={`exp-${exp.id}-like`}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition ${
            liked ? "text-coral" : "text-ink-mid hover:text-coral hover:bg-cream"
          }`}
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-coral" : ""}`} />
          {likes}
        </button>
        <button
          onClick={() => setShowComments((s) => !s)}
          data-testid={`exp-${exp.id}-toggle-comments`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-ink-mid hover:text-coral hover:bg-cream"
        >
          <MessageCircle className="w-4 h-4" />
          {exp.comments_count + comments.filter(c => !c.id.startsWith('seed')).length || 0}
        </button>
      </footer>

      {/* Comments */}
      {showComments && (
        <div className="border-t hairline bg-cream/40 p-3 space-y-3" data-testid={`exp-${exp.id}-comments`}>
          {comments.length === 0 && (
            <p className="text-[12px] text-ink-mid text-center py-2">Sé el primero en comentar.</p>
          )}
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-coral/15 grid place-items-center text-[10px] font-mono text-coral shrink-0 overflow-hidden">
                {c.user_avatar
                  ? <img src={c.user_avatar} alt="" className="w-full h-full object-cover" />
                  : (c.user_name?.[0]?.toUpperCase() || "•")}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px]">
                  <span className="font-medium">{c.user_name}</span>{" "}
                  <span className="text-ink-mid">· {fmtRelative(c.created_at)}</span>
                </p>
                <p className="text-sm text-ink whitespace-pre-line">{c.body}</p>
              </div>
            </div>
          ))}
          <form onSubmit={submitComment} className="flex gap-2 pt-1">
            <input
              data-testid={`exp-${exp.id}-comment-input`}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Escribe un comentario…"
              className="flex-1 bg-cream-card border hairline rounded-full px-3 py-1.5 text-xs outline-none focus:border-coral"
            />
            <button
              type="submit"
              disabled={posting || !commentText.trim()}
              data-testid={`exp-${exp.id}-comment-submit`}
              className="bg-ink text-cream rounded-full px-3 grid place-items-center hover:bg-coral transition disabled:opacity-50"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </form>
        </div>
      )}
    </article>
  );
}
